/** Personal to-dos (#46): a private list for each person. Shared types and grouping so web and API agree. */
import { DateTime } from 'luxon';

export const TODO_TZ = 'Europe/London';
/** Date-only to-dos are reminded relative to this time on the day. */
export const TODO_DAY_TIME = '09:00';

export interface TodoSource {
  message_id: string;
  conversation_id: string;
  /** "#general" or the DM name; null when you can no longer see the conversation. */
  conversation_name: string | null;
  preview: string | null;
  link: string | null;
}

export interface Todo {
  id: string;
  parent_id: string | null;
  title: string;
  notes: string | null;
  /** YYYY-MM-DD, UK date. */
  due_date: string | null;
  /** HH:mm, UK time; only with a due date. */
  due_time: string | null;
  /** Minutes before the due time (date-only: before 09:00 on the day); null = no reminder. */
  reminder_minutes: number | null;
  remind_at: string | null;
  done_at: string | null;
  position: number;
  source: TodoSource | null;
  subtasks: Todo[];
  /** Set when it was turned into a task (#47). */
  task_id?: string | null;
  /** The task this to-do reminds you about, while you can still see it. */
  task?: { id: string; key: string; title: string; link: string } | null;
  created_at: string;
  updated_at: string;
}

export interface TodoInput {
  title?: string;
  notes?: string | null;
  due_date?: string | null;
  due_time?: string | null;
  reminder_minutes?: number | null;
  parent_id?: string | null;
  source_message_id?: string | null;
  done?: boolean;
}

export const TODO_LIMITS = { title: 500, notes: 10_000, subtasks: 50, open: 1000 } as const;

/** Reminder choices shown in the editor. */
export const TODO_REMINDERS: Array<{ minutes: number; label: string; timedOnly?: boolean }> = [
  { minutes: 0, label: 'At the time' },
  { minutes: 15, label: '15 minutes before', timedOnly: true },
  { minutes: 60, label: '1 hour before', timedOnly: true },
  { minutes: 1440, label: '1 day before' },
];

/** When a reminder fires, as an ISO instant, or null. Date-only to-dos use 09:00 UK time on the day. */
export function todoRemindAt(t: Pick<Todo, 'due_date' | 'due_time' | 'reminder_minutes'>): string | null {
  if (!t.due_date || t.reminder_minutes == null) return null;
  const at = DateTime.fromISO(`${t.due_date}T${t.due_time ?? TODO_DAY_TIME}`, { zone: TODO_TZ });
  if (!at.isValid) return null;
  return at.minus({ minutes: t.reminder_minutes }).toUTC().toISO();
}

export type TodoGroup = 'today' | 'upcoming' | 'nodate' | 'done';
export const TODO_GROUP_LABELS: Record<TodoGroup, string> = { today: 'Today', upcoming: 'Upcoming', nodate: 'No date', done: 'Done' };

/**
 * Today (including anything overdue), Upcoming, No date and Done. Today and No date keep the given (manual, dragged)
 * order; Upcoming reads by date and time; Done shows the most recently finished first.
 */
/** Groups whose order the owner sets by dragging. */
export const TODO_MANUAL_GROUPS: TodoGroup[] = ['today', 'nodate'];

export function groupTodos(todos: Todo[], today: string): Record<TodoGroup, Todo[]> {
  const out: Record<TodoGroup, Todo[]> = { today: [], upcoming: [], nodate: [], done: [] };
  for (const t of todos) {
    if (t.done_at) out.done.push(t);
    else if (!t.due_date) out.nodate.push(t);
    else if (t.due_date <= today) out.today.push(t);
    else out.upcoming.push(t);
  }
  const byDue = (a: Todo, b: Todo) => (a.due_date ?? '').localeCompare(b.due_date ?? '') || (a.due_time ?? '99').localeCompare(b.due_time ?? '99') || a.position - b.position;
  out.upcoming.sort(byDue);
  out.done.sort((a, b) => (b.done_at ?? '').localeCompare(a.done_at ?? ''));
  return out;
}

/** "Overdue · Mon 12 Oct", "Today 14:00", "Tomorrow", "Fri 23 Oct 09:30". */
export function todoDueLabel(t: Pick<Todo, 'due_date' | 'due_time'>, today: string): string | null {
  if (!t.due_date) return null;
  const d = DateTime.fromISO(t.due_date, { zone: TODO_TZ }).setLocale('en-GB');
  const tomorrow = DateTime.fromISO(today, { zone: TODO_TZ }).plus({ days: 1 }).toISODate();
  const day = t.due_date === today ? 'Today' : t.due_date === tomorrow ? 'Tomorrow' : d.toFormat('ccc d LLL');
  const label = t.due_time ? `${day} ${t.due_time}` : day;
  return t.due_date < today ? `Overdue · ${label}` : label;
}

/** Where a task opens: its project board, or the Tasks area for a task with no project (backlog #47). */
/** t171u: a bug report or suggestion, as its reporter sees it (Help › My submissions). */
export const feedbackHref = (taskId: string) => `/help/feedback/${taskId}`;
export function taskHref(t: { id: string; project_id: string; no_project?: boolean | null }): string {
  return t.no_project ? `/tasks?task=${t.id}` : `/projects/${t.project_id}?task=${t.id}`;
}
